import UploadClient from '@uploadcare/upload-client';
import ApiService from '../../../util/api';
import featureFlags from '../../../util/featureFlags';
import { FILE_TYPE_COPY } from './widgetFileTypeCopy';

export const uploadcareKey =
  window.zola && window.zola.env === 'production' ? 'bed481ea2bef6baf44de' : 'debf625b3ed8063e511f';

const uploadcareClient = new UploadClient({ publicKey: uploadcareKey });

export const isUploadcareEnabled = featureFlags.get('uploadcareWidget') && !!uploadcareKey;

// TODO: The retry here is a temporary fix until svc-image implements it. Remove it here after.
const MAX_COPY_ATTEMPTS = 2;
export const copyUploadcareFileToZola = (uploadcareFile, attemptNb = 0) => {
  return ApiService.post('/web-api/v1/image/uploadcare-save', {
    url: uploadcareFile.cdnUrl,
    contentType: uploadcareFile.mimeType,
  }).then(json => {
    const data = json && json.data;

    if (!data && attemptNb < MAX_COPY_ATTEMPTS) {
      return copyUploadcareFileToZola(uploadcareFile, attemptNb + 1);
    }

    return data;
  });
};

export const copyUploadcareFileGroupToZola = fileGroupId => {
  return ApiService.post('/web-api/v1/image/uploadcare-group-save', {
    groupId: fileGroupId,
  }).then(json => json.data);
};

export const fetchImageMetadata = zolaImageId => {
  return ApiService.get(`/web-api/v1/image/metadata/${zolaImageId}`).then(json => json.data);
};

export const fetchOriginalMetadata = uploadcareImageId => {
  return ApiService.get(`/web-api/v1/image/uploadcare-original/${uploadcareImageId}`)
    .then(json => json.data)
    .catch(() => ({})); // Return empty data if there is an error fetching the original
};

// Uploadcare allows us to fetch the original file data, but doesn't return modifier info,
// We must therefore manually add modiifiers to the returned uploadcare file.
export const fetchUploadcareFile = cdnUrl => {
  const urlMatches = cdnUrl.match(/ucarecdn\.com\/([^/]+)\/(-.+)?/);
  const cropMatches = cdnUrl.match(/-\/crop\/(\d+)x(\d+)\/(\d+),(\d+)/);

  const id = urlMatches[1];
  const cdnUrlModifiers = urlMatches[2];
  const crop = cropMatches
    ? {
        width: cropMatches[1],
        height: cropMatches[2],
        x: cropMatches[3],
        y: cropMatches[4],
      }
    : null;

  // Doesn't actually reupload: Simply returns existing upload's data.
  return uploadcareClient.uploadFile(id).then(uploadcareFile => {
    return {
      ...uploadcareFile,
      cdnUrl,
      cdnUrlModifiers,
      crop,
      originalImageInfo: uploadcareFile.imageInfo,
    };
  });
};

export const formatBytes = bytes => {
  const kb = 1024;
  return +(bytes / kb / kb).toFixed(2);
};

const fixGrayscaleOptions = url => {
  return (
    url &&
    url
      .replace(/-\/max_icc_size\/0\//, '')
      .replace(/-\/grayscale\//, '-/grayscale/-/max_icc_size/0/')
  );
};

// HACK: Uploadcare currently doesn't strip the original color profile when greyscale is applied.
// As a temp fix, they suggested adding `max_icc_size/0` when greyscale is enabled.
export const clearICCProfile = uploadcareFile => ({
  ...uploadcareFile,
  cdnUrl: fixGrayscaleOptions(uploadcareFile.cdnUrl),
  cdnUrlModifiers: fixGrayscaleOptions(uploadcareFile.cdnUrlModifiers),
});

/**
 * Recursively formats all strings contained in an object, replacing template
 * variables (in curly braces) with their corresponding values (if available).
 * @param {(Object|String)} currentNode - A translation object or string. Strings can contain template variables in curly braces
 * @param {Object} replacements - An object with properties for each template variable to replace
 * @returns {(Object|String)} - A formatted version of currentNode
 */
export const replaceTemplateVariables = (currentNode, replacements) => {
  if (typeof currentNode === 'string') {
    let formatted = currentNode;

    Object.keys(replacements).forEach(key => {
      formatted = formatted.replace(`{${key}}`, replacements[key]);
    });

    return formatted;
  }

  const newNode = {};

  Object.keys(currentNode).forEach(key => {
    newNode[key] = replaceTemplateVariables(currentNode[key], replacements);
  });

  return newNode;
};

// Returns a string enumerating all accepted file types
export const getFileTypeCopy = (fileTypes = []) => {
  // Empty
  if (!fileTypes.length) return '';

  // Single
  if (fileTypes.length === 1) return FILE_TYPE_COPY[fileTypes[0]];

  // Multiples. Insert a comma between every item except the last, which uses the word "or".
  const fileTypeNames = fileTypes.map(type => FILE_TYPE_COPY[type]);
  const allButLast = fileTypeNames.slice(0, -1);
  const last = fileTypeNames.slice(-1);
  return [allButLast.join(', '), ...last].join(' or ');
};

const withoutCrop = url => (url || '').replace(/-\/crop\/[0-9]+x[0-9]+\/[0-9]+,[0-9]+\//, '');

export const autocropUploadcareFile = (uploadcareFile, imageElement) => {
  const {
    cdnUrlModifiers,
    crop: currentCrop,
    originalImageInfo: { height: fileHeight, width: fileWidth },
    originalUrl,
  } = uploadcareFile;
  const { height: targetHeight, width: targetWidth } = imageElement;
  const targetRatio = targetWidth / targetHeight;

  // If the files current crop already has the correct aspect ratio, let it be.
  if (currentCrop && currentCrop.width / currentCrop.height === targetRatio) {
    return uploadcareFile;
  }

  const fileRatio = fileWidth / fileHeight;
  const imageIsPortrait = fileRatio < 1;
  let height;
  let width;

  // Base the default crop size on the image's smallest dimension (width or height)
  if (imageIsPortrait) {
    width = Math.min(fileWidth * targetRatio, fileWidth);
    height = width / targetRatio;
  } else {
    height = Math.min(fileHeight / targetRatio, fileHeight);
    width = height * targetRatio;
  }

  // Only integers are accepted in Uploadcare url modifiers, so round the cropped dimensions
  width = Math.round(width);
  height = Math.round(height);

  // Center the crop zone
  const left = Math.round((fileWidth - width) / 2);
  const top = Math.round((fileHeight - height) / 2);

  // Update cdnUrlModifiers by removing the (optional) old crop param and adding the new one
  const newModifiers = `-/crop/${width}x${height}/${left},${top}/${withoutCrop(cdnUrlModifiers)}`;

  return {
    ...uploadcareFile,
    crop: { width, height, left, top },
    cdnUrl: `${originalUrl}${newModifiers}`,
    cdnUrlModifiers: newModifiers,
  };
};



// WEBPACK FOOTER //
// ./src/components/common/UploadcareWidget/utils.js